import type { CSSProperties } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { useDashboardQuery, useHomepageQuery } from "../../Redux/Apis/authApi";
import { useGetAllExamCategoriesQuery, useGetAllExamsQuery } from "../../Redux/Apis/examApi";
import { useGetDueQuery } from "../../Redux/Apis/leitnerApi";
import { fa_dateـetime, fa_number } from "../../utils/Persian";
import Icon, { type IconName } from "../../Components/Icon";

const blogFallback = [
  {
    id: "placeholder-1",
    title: "۸ اشتباه رایج در Quant",
    image: "https://picsum.photos/96?1",
    meta: "دیروز • ۳ دقیقه",
    link: "#",
  },
  {
    id: "placeholder-2",
    title: "راهنمای کامل Text Completion",
    image: "https://picsum.photos/96?2",
    meta: "۲ روز پیش • ۵ دقیقه",
    link: "#",
  },
  {
    id: "placeholder-3",
    title: "مدیریت زمان در آزمون واقعی",
    image: "https://picsum.photos/96?3",
    meta: "۳ روز پیش • ۴ دقیقه",
    link: "#",
  },
];

type DashboardCategoryCard = {
  id: number | string;
  title: string;
  subtitle?: string;
  background: string;
  link: string;
  examCount?: number;
  iconName?: IconName;
  iconClass?: string;
};

function getFaIconName(iconClass?: string): string | null {
  if (!iconClass) return null;
  const parts = iconClass.split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  const last = parts[parts.length - 1];
  if (!last.startsWith("fa-")) return null;
  return last.replace(/^fa-/, "") || null;
}

function Dashboard() {
  const { data: dashboardData } = useDashboardQuery();
  const { data: homepageData } = useHomepageQuery();
  const { data: examCategories } = useGetAllExamCategoriesQuery();
  const { data: examsData } = useGetAllExamsQuery();
  const { data: leitnerDueData } = useGetDueQuery({ limit: 6 });

  const pendingExams = dashboardData?.pendingExams ?? [];
  const pendingExamsSorted = pendingExams
    .slice()
    .sort((a, b) => new Date(b.create_date).getTime() - new Date(a.create_date).getTime());
  const activeExam = pendingExamsSorted.length ? pendingExamsSorted[0] : undefined;

  const categoryBackgrounds = ["#FFE4E6", "#E0F2FE", "#DCFCE7", "#FFF7ED", "#F3E8FF"];

  const rawCategories = (examCategories ?? []).slice();

  const parentIds = new Set<number>();
  rawCategories.forEach((category) => {
    if (category.parent_id != null) {
      parentIds.add(category.parent_id);
    }
  });

  const leafCategories = rawCategories.filter((category) => !parentIds.has(category.id));

  const apiCategories: DashboardCategoryCard[] =
    leafCategories
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((category, index) => ({
        id: category.id,
        title: category.name,
        subtitle: category.ParentCategory?.name ?? "",
        examCount:
          typeof category.exam_count === "number"
            ? category.exam_count
            : (examsData ?? []).filter((exam) => exam.cat_id === category.id).length,
        background: categoryBackgrounds[index % categoryBackgrounds.length],
        iconClass: category.icon,
        link: `/panel/exams?category=${category.id}`,
      }));

  const freeExamCount = (examsData ?? []).filter((exam) => exam.is_free).length;

  const categories: DashboardCategoryCard[] = [
    ...apiCategories,
    {
      id: "FREE",
      title: "هدیه",
      subtitle: "آزمون‌های رایگان",
      examCount: freeExamCount,
      background: "#E5E7EB",
      iconName: "gift" as IconName,
      link: "/panel/exams?free=1",
    },
  ];

  const blogItems = (homepageData?.LastBlog ?? []).slice(0, 4).map((item: any) => ({
    id: item.id,
    title: item.title,
    image: item.image,
    link: `/post/${item.id}/${item.title?.replaceAll(' ', '-').toLowerCase()}`,
    meta: item?.BlogCategory?.name ?? "بلاگ",
  }));

  const blogList = blogItems.length ? blogItems : blogFallback;

  const recentPendingExams = pendingExamsSorted.slice(0, 4);

  const vocabDueCount = leitnerDueData?.dueCount ?? 0;
  const vocabChips = (leitnerDueData?.items ?? []).slice(0, 6).map((card) => card.front);

  const heroRingStyle: CSSProperties = {
    ['--panel-ring-value' as any]: activeExam ? '62' : '18',
    ['--panel-ring-fill' as any]: '#ffd166',
    ['--panel-ring-size' as any]: '64px',
  };

  return (
    <main className="col-lg-9">
      {/* Hero */}
      <div className="panel-surface p-3 bg-primary text-white border-0 rounded-4 mb-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="panel-ring" style={heroRingStyle} />
            <div>
              <h5 className="m-0 fw-bold">
                {activeExam ? `ادامه آخرین آزمون • ${activeExam.Exam?.name}` : "آزمون جدید شروع کنید"}
              </h5>
              <div className="small">
                {activeExam
                  ? `شروع شده در ${fa_dateـetime(activeExam.create_date)}`
                  : "از میان دسته‌بندی‌های زیر یکی را انتخاب کنید و تمرین را ادامه دهید."}
              </div>
            </div>
          </div>
          {activeExam ? (
            <Link to={`/panel/exam/real/${activeExam.Exam?.id}`} className="btn btn-success fw-bold px-4 d-flex align-items-center gap-2">
              ادامه
              <Icon name="caret-left-fill" size={16} />
            </Link>
          ) : (
            <Link to="/panel/exams" className="btn btn-light fw-bold px-4 d-flex align-items-center gap-2">
              مشاهده آزمون‌ها
              <Icon name="caret-left" size={16} />
            </Link>
          )}
        </div>
      </div>

      {/* Categories + Blog */}
      <div className="row g-3">
        <div className="col-lg-8">
          <div className="panel-surface p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="m-0 fw-bold">دسته‌بندی آزمون‌ها</h6>
              <span className="panel-muted small">شروع سریع</span>
            </div>
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3">
              {categories.map((category) => (
                <div className="col" key={category.id}>
                  <div className="card h-100 rounded-4 border-0 position-relative" style={{ background: category.background }}>
                    {typeof category.examCount === "number" && (
                      <span className="badge bg-white text-dark border position-absolute top-0 start-0 m-2">
                        {fa_number(category.examCount)}
                      </span>
                    )}
                    <div className="card-body d-flex flex-column justify-content-between">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="panel-icon-circle text-danger">
                          {category.iconName ? (
                            <Icon name={category.iconName} size={20} strokeWidth={1.4} />
                          ) : getFaIconName(category.iconClass) ? (
                            <FontAwesomeIcon icon={getFaIconName(category.iconClass) as any} className="fs-5" />
                          ) : (
                            <Icon name="layout" size={20} strokeWidth={1.4} />
                          )}
                        </span>
                        {category.subtitle && (
                          <span className="small panel-muted">{category.subtitle}</span>
                        )}
                      </div>
                      <div className="text-center">
                        <h6 className="fw-bold mt-2 mb-3">{category.title}</h6>
                        <Link className="btn btn-primary btn-sm px-3" to={category.link}>
                          مشاهده
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="panel-surface p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="fw-bold m-0">
                <Icon name="newspaper" className="ms-1 text-danger" size={18} strokeWidth={1.4} /> آخرین‌های بلاگ
              </h6>
              <Link className="small text-decoration-none" to="/blog">
                همه
              </Link>
            </div>
            <hr className="my-2" />
            {blogList.map((blog) => (
              <div className="d-flex gap-2 align-items-start mb-1" key={blog.id}>
                <div className="ratio ratio-1x1 rounded-3 overflow-hidden flex-shrink-0" style={{ width: "64px" }}>
                  <img src={blog.image} className="w-100 h-100 object-fit-cover" alt={blog.title} />
                </div>
                <div className="flex-grow-1">
                  <Link to={blog.link} className="text-decoration-none fw-bold d-block">
                    {blog.title}
                  </Link>
                  <small className="panel-muted">{blog.meta}</small>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

	      {/* Pending + Vocabulary */}
	      <div className="row g-3 mt-1">
	        <div className="col-lg-6">
	          <div className="panel-surface p-3 h-100">
	            <div className="d-flex justify-content-between align-items-center mb-2">
	              <h6 className="fw-bold m-0">آخرین آزمون‌های در حال انجام</h6>
	              <Icon name="speedometer" className="text-primary" size={18} strokeWidth={1.4} />
	            </div>
	            {recentPendingExams.length ? (
	              <div className="table-responsive">
	                <table className="table align-middle m-0">
	                  <thead>
	                    <tr className="text-secondary small">
	                      <th>آزمون</th>
	                      <th>شروع</th>
	                      <th></th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {recentPendingExams.map((row, index) => {
	                      return (
	                        <tr key={`${row.Exam?.name ?? 'pending'}-${index}`}>
	                          <td>{row.Exam?.name ?? "-"}</td>
	                          <td>{row.create_date ? fa_dateـetime(row.create_date) : "—"}</td>
	                          <td className="text-end">
	                            <Link
	                              to={row.Exam?.id ? `/panel/exam/real/${row.Exam.id}` : "/panel/exams"}
	                              className="btn btn-success btn-sm d-inline-flex align-items-center gap-1"
	                            >
	                              ادامه
	                              <Icon name="arrow-up-right" size={14} strokeWidth={1.4} />
	                            </Link>
	                          </td>
	                        </tr>
	                      );
	                    })}
	                  </tbody>
	                </table>
	              </div>
	            ) : (
	              <div className="text-center py-4 panel-muted">
	                آزمونِ در حال انجام ندارید.
	                <div className="mt-2">
	                  <Link to="/panel/exams" className="btn btn-primary btn-sm">
	                    شروع آزمون جدید
	                  </Link>
	                </div>
	              </div>
	            )}
	          </div>
	        </div>

        <div className="col-lg-6">
          <div className="panel-surface p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold m-0">واژگان • سررسید امروز: {fa_number(vocabDueCount)}</h6>
              <Icon name="collection" size={18} strokeWidth={1.4} />
            </div>
            <div className="mb-2">
              <Link className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" to="/panel/leitner/study">
                <Icon name="lightning" size={16} strokeWidth={1.4} />
                شروع مرور
              </Link>
            </div>
            {vocabChips.length ? (
              <div className="d-flex flex-wrap gap-2">
                {vocabChips.map((word) => (
                  <span className="panel-chip-ghost" key={word}>
                    {word}
                  </span>
                ))}
              </div>
            ) : (
              <div className="panel-muted small">امروز لغتی برای مرور وجود ندارد.</div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
